/**
 * Estado operativo: nodos de OpenWA, colas y registro de auditoría.
 *
 * Las tres listas de esta pantalla llegan como filas crudas de PostgreSQL —
 * `base_url`, `active_sessions`, `created_at`, `actor_email`— y la vista las
 * leía en camelCase. El resultado no era una pantalla vacía, que se habría
 * notado: era una pantalla LLENA de valores por defecto. Los nodos titulados con
 * su UUID porque `name` no existe y el respaldo era `id`; «0 / 0 sesiones» sobre
 * nodos con carga real; la auditoría sin fecha ni autor; y, lo peor, un semáforo
 * que caía en verde para cualquier estado que no supiera leer.
 *
 * Igual que en `model-catalog.js`, la traducción vive aquí y no en la plantilla,
 * y es lista blanca: la fila de un nodo trae `api_key_env` y la de auditoría
 * trae `before`/`after` con cargas útiles completas. Nada de eso tiene por qué
 * llegar a una vista sólo porque venga en la respuesta.
 */

/** Estados que el DDL permite en un nodo (`002_platform_catalog.sql`). */
export const NODE_STATUSES = Object.freeze([
  'unknown',
  'healthy',
  'degraded',
  'down',
  'draining',
])

/**
 * Descriptores de estado de un nodo.
 *
 * `unknown` es un estado REAL del backend y además el valor por defecto de la
 * columna, no una anomalía del panel. Antes ni siquiera estaba en el mapa: un
 * nodo recién dado de alta, que es exactamente el que nadie ha comprobado
 * todavía, se pintaba «Correcto» en verde.
 */
const NODE_STATUS_DESCRIPTORS = Object.freeze({
  healthy: { labelKey: 'platform.nodeStatusHealthy', tone: 'success' },
  degraded: { labelKey: 'platform.nodeStatusDegraded', tone: 'warning' },
  down: { labelKey: 'platform.nodeStatusDown', tone: 'danger' },
  draining: { labelKey: 'platform.nodeStatusDraining', tone: 'info' },
  unknown: { labelKey: 'platform.nodeStatusUnknown', tone: 'neutral' },
})

/** @param {unknown} value */
export function describeNodeStatus(value) {
  const key = NODE_STATUSES.includes(value) ? value : 'unknown'
  return { key, ...NODE_STATUS_DESCRIPTORS[key] }
}

/**
 * Nodo de OpenWA presentable.
 *
 * El título es el `code` (`openwa-1`), que es como se llama al nodo cuando hay
 * que reiniciarlo. El UUID no es un nombre: es lo que salía antes y no le dice
 * nada a quien está mirando esta pantalla precisamente porque algo va mal.
 *
 * @param {Record<string, unknown>|null|undefined} raw
 */
export function normalizeOpenwaNode(raw) {
  const source = object(raw)
  const code = text(pick(source, 'code'))
  return {
    id: text(pick(source, 'id')) || code,
    code,
    baseUrl: nullable(pick(source, 'baseUrl', 'base_url')),
    engine: nullable(pick(source, 'engine')),
    // `null` y no `0`: un nodo sin capacidad declarada y uno con capacidad cero
    // son cosas distintas, y sólo la segunda significa «no admite sesiones».
    capacity: number(pick(source, 'capacity')),
    activeSessions: number(pick(source, 'activeSessions', 'active_sessions')),
    status: text(pick(source, 'status')) || 'unknown',
    lastHealthAt: nullable(pick(source, 'lastHealthAt', 'last_health_at')),
  }
}

/**
 * Ocupación del nodo en tanto por uno.
 *
 * `null` cuando falta cualquiera de los dos datos o la capacidad no es
 * positiva. La versión anterior hacía `activeSessions / Math.max(1, capacity)`,
 * que nunca falla y por eso siempre miente: con capacidad desconocida pintaba
 * una barra al 0 % que se lee como «este nodo está libre».
 *
 * @param {{ activeSessions: number|null, capacity: number|null }} node
 */
export function nodeLoadRatio(node = {}) {
  const { activeSessions, capacity } = node
  if (activeSessions === null || capacity === null || capacity <= 0) return null
  return activeSessions / capacity
}

/**
 * Entrada del registro de auditoría.
 *
 * Quién hizo qué, cuándo y sobre qué. Los cuatro datos existen en la tabla desde
 * el primer día; la vista los buscaba en `entry.createdAt`, `entry.actor.email`,
 * `entry.organization.name` y `entry.entity`, y ninguno de esos caminos existe.
 *
 * `before`/`after` se quedan FUERA a propósito: son cargas útiles completas de
 * la entidad modificada y pueden contener datos personales del cliente. La regla
 * 10 del proyecto dice que los registros se ofuscan; volcarlos en el panel sería
 * saltársela por la puerta de atrás.
 *
 * @param {Record<string, unknown>|null|undefined} raw
 */
export function normalizeAuditEntry(raw) {
  const source = object(raw)
  const entityType = text(pick(source, 'entityType', 'entity_type'))
  const entityId = text(pick(source, 'entityId', 'entity_id'))

  return {
    id: text(pick(source, 'id')),
    action: text(pick(source, 'action')),
    // El correo del autor es lo que identifica a una persona en este registro;
    // `actor_type` (`user`, `platform`, `system`) es el respaldo cuando no hay
    // persona detrás, que es el caso de las acciones automáticas.
    actorEmail: nullable(pick(source, 'actorEmail', 'actor_email')),
    actorType: nullable(pick(source, 'actorType', 'actor_type')),
    actorRole: nullable(pick(source, 'actorRole', 'actor_role')),
    organizationId: nullable(pick(source, 'organizationId', 'organization_id')),
    // A QUÉ CLIENTE afectó. El `JOIN` es LEFT a propósito: las acciones de
    // plataforma que no tocan a ningún cliente concreto siguen apareciendo con
    // el nombre en `null`, y esconderlas sería un agujero en la auditoría.
    organizationName: nullable(pick(source, 'organizationName', 'organization_name')),
    organizationSlug: nullable(pick(source, 'organizationSlug', 'organization_slug')),
    entityType: entityType || null,
    entityId: entityId || null,
    status: nullable(pick(source, 'status')),
    createdAt: nullable(pick(source, 'createdAt', 'created_at')),
    // ¿Se hizo suplantando a un cliente? Es justo lo que se viene a mirar aquí.
    impersonated: pick(source, 'impersonationSessionId', 'impersonation_session_id') !== null,
  }
}

/**
 * Fila de una cola de trabajo.
 *
 * Los cuatro contadores son medidas reales de Redis (`LLEN`/`ZCARD`), así que un
 * cero aquí SÍ es un cero: la cola está vacía. Lo que nunca puede pasar es que
 * un contador ausente se pinte como cero, y por eso pasan por `number()`.
 *
 * @param {Record<string, unknown>|null|undefined} raw
 */
export function normalizeQueue(raw) {
  const source = object(raw)
  return {
    name: text(pick(source, 'name')),
    waiting: number(pick(source, 'waiting')),
    active: number(pick(source, 'active')),
    delayed: number(pick(source, 'delayed')),
    failed: number(pick(source, 'failed')),
  }
}

/**
 * Estado de las colas, con la distinción que el contrato existe para hacer.
 *
 * El backend NO devuelve una lista vacía cuando no ha podido mirar: devuelve
 * `queues: null` con `measured: false` y el motivo. La diferencia es justo la
 * que importa en esta pantalla: `[]` dice «he mirado y no hay nada encolado»
 * —un sistema ocioso, buena noticia— y `null` dice «no he podido mirar»,
 * posiblemente un sistema parado con el buzón lleno. Enseñar el mejor escenario
 * en el peor momento es exactamente lo que se está corrigiendo.
 *
 * `notMeasured` viaja entero: la cola de SALIDA vive en memoria del trabajador y
 * la API no la ve. Omitirla en silencio dejaría a un operador creyendo que la
 * lista de una cola es la lista de todas.
 *
 * @param {Record<string, unknown>|null|undefined} raw
 */
export function normalizeQueuesStatus(raw) {
  const source = object(raw)
  const rows = source.queues

  return {
    // `null` se conserva como `null`. Convertirlo a `[]` aquí borraría toda la
    // información que el backend se molestó en publicar.
    items: Array.isArray(rows) ? rows.map(normalizeQueue) : null,
    measured: source.measured === true,
    reason: nullable(pick(source, 'reason')),
    notMeasured: Array.isArray(source.notMeasured)
      ? source.notMeasured.map((entry) => ({
          name: text(pick(object(entry), 'name')),
          reason: nullable(pick(object(entry), 'reason')),
        }))
      : [],
    updatedAt: nullable(pick(source, 'updatedAt', 'updated_at')),
  }
}

/**
 * Entregas de webhook fallidas: hoy NO SE MIDEN, y el backend lo dice.
 *
 * Llega `items: null` con `measured: false` y el motivo. No existe tabla de
 * intentos ni de fallos de entrega —se buscó en las 18 migraciones—, así que
 * esta lista no puede estar ni vacía ni llena. La lista vacía anterior convertía
 * la ausencia de instrumentación en un parte de buena salud.
 *
 * @param {Record<string, unknown>|null|undefined} raw
 */
export function normalizeWebhookFailures(raw) {
  const source = Array.isArray(raw) ? { items: raw, measured: true } : object(raw)
  const rows = source.items

  return {
    items: Array.isArray(rows) ? rows : null,
    measured: source.measured === true,
    reason: nullable(pick(source, 'reason')),
  }
}

/* ─── Utilidades internas ─────────────────────────────────────────────────── */

function pick(source, ...names) {
  for (const name of names) {
    if (source[name] !== undefined && source[name] !== null) return source[name]
  }
  return null
}

function object(value) {
  return value && typeof value === 'object' ? value : {}
}

function text(value) {
  if (typeof value === 'string') return value
  return typeof value === 'number' ? String(value) : ''
}

function nullable(value) {
  const result = text(value)
  return result === '' ? null : result
}

/** `null` cuando no hay dato, para poder distinguirlo de un cero real. */
function number(value) {
  if (value === null || value === undefined || value === '') return null
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : null
}
